import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import config from '../../config';
import { useGlobalContext } from '../../GlobalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTree, faUsers, faChartLine, faDownload, faRefresh, faCrown, faMedal, faTrophy, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './MatrixGenealogyTree.css'; // We'll create this CSS file

export default function MatrixGenealogyTree() {
  const {
    isLoading,
    setIsLoading,
    isAppError,
    setAppError,
    appErrorMessage,
    setAppErrorMessage,
    appErrorTitle,
    setAppErrorTitle,
    appErrorMode,
    setAppErrorMode,
    appUser
  } = useGlobalContext();

  // State management
  const [userId, setUserId] = useState(1);
  const [maxDepth, setMaxDepth] = useState(10);
  const [maxChildren, setMaxChildren] = useState(10);
  const [treeData, setTreeData] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [viewMode, setViewMode] = useState('basic'); // 'basic' or 'stats'
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Fetch matrix tree data
  const fetchMatrixTree = async () => {
    if (!userId) {
      setAppError(true);
      setAppErrorTitle("Validation Error");
      setAppErrorMessage("Please enter a valid User ID");
      setAppErrorMode("error");
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = viewMode === 'stats' 
        ? `${config.apiUrl}user-refer/binary-genealogy-tree-with-stats?mobile=${userId}&maxDepth=${maxDepth}`
        : `${config.apiUrl}user-refer/binary-genealogy-tree?mobile=${userId}`;
      
      const response = await axios.get(endpoint);
      
      if (response.data.success) {
        setTreeData(response.data.tree);
        setStatistics(response.data.statistics || null);
        setAppError(true);
        setAppErrorTitle("Success");
        setAppErrorMessage(`Matrix tree loaded successfully for User ID: ${userId}`);
        setAppErrorMode("success");
      } else {
        setAppError(true);
        setAppErrorTitle("Error");
        setAppErrorMessage(response.data.message || "Failed to fetch tree data");
        setAppErrorMode("error");
      }
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Network Error");
      setAppErrorMessage("Failed to connect to the API. Please check your connection.");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();
    fetchMatrixTree();
  };

  // Clear results
  const clearResults = () => {
    setTreeData(null);
    setStatistics(null);
    setExpandedNodes(new Set());
  };

  // Toggle node details
  const toggleNodeDetails = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Export tree data
  const exportTreeData = () => {
    if (!treeData) {
      setAppError(true);
      setAppErrorTitle("Export Error");
      setAppErrorMessage("No tree data to export. Please load a tree first.");
      setAppErrorMode("error");
      return;
    }

    const dataToExport = {
      userId,
      tree: treeData,
      statistics,
      exportedAt: new Date().toISOString(),
      exportedBy: appUser?.name || 'Unknown User'
    };

    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `matrix-tree-${userId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setAppError(true);
    setAppErrorTitle("Export Success");
    setAppErrorMessage("Tree data exported successfully!");
    setAppErrorMode("success");
  };

  // Collect nodes by level for matrix display
  const collectNodesByLevel = (node, level = 0, levels = {}, visited = new Set()) => {
    if (!node || visited.has(node.id)) return levels;
    
    visited.add(node.id);
    
    if (!levels[level]) {
      levels[level] = [];
    }
    
    levels[level].push(node);
    
    // Process children
    if (node.leftChild) {
      collectNodesByLevel(node.leftChild, level + 1, levels, visited);
    }
    if (node.rightChild) {
      collectNodesByLevel(node.rightChild, level + 1, levels, visited);
    }
    
    return levels;
  };

  // Render a single matrix node
  const renderMatrixNode = (node, level) => {
    if (!node) return null;

    const isRoot = level === 0;
    const isInactive = node.status !== 'active';
    const isExpanded = expandedNodes.has(node.id);
    const childrenCount = (node.leftChild ? 1 : 0) + (node.rightChild ? 1 : 0);
    
    let nodeClasses = ['matrix-node'];
    if (isRoot) {
      nodeClasses.push('root-node');
    } else if (isInactive) {
      nodeClasses.push('inactive-user');
    } else {
      nodeClasses.push(`level-${Math.min(level, 5)}`);
    }

    const joinDate = new Date(node.createdDateTime);
    const daysSinceJoin = Math.floor((new Date() - joinDate) / (1000 * 60 * 60 * 24));

    return (
      <div key={node.id} className={nodeClasses.join(' ')} onClick={() => toggleNodeDetails(node.id)}>
        {/* Node Header */}
        <div className="node-header">
          <div className="node-id">
            {isRoot ? <FontAwesomeIcon icon={faCrown} /> : <FontAwesomeIcon icon={faUsers} />}
            {' '}{node.name}
          </div>
          <div className="node-sub-id">ID: {node.id}</div>
        </div>

        {/* Node Body */}
        <div className="node-body">
          <div className="node-info">
            <i className="ri-phone-line"></i>
            <a 
              href={`tel:+91${node.mobile}`} 
              className="mobile-link"
              onClick={(e) => e.stopPropagation()}
              title={`Call +91${node.mobile}`}
            >
              {node.mobile}
            </a>
          </div>
          <div className="node-info">
            <i className="ri-mail-line"></i>
            <a 
              href={`mailto:${node.email}`} 
              className="email-link"
              onClick={(e) => e.stopPropagation()}
              title={node.email}
            >
              {node.email ? node.email.substring(0, 6) + '...' : 'No email'}
            </a>
          </div>
          <div className="node-info">
            <i className="ri-user-line"></i>
            <span>{node.referUserName || 'Root User'}</span>
          </div>
          <div className="node-info">
            <i className="ri-calendar-line"></i>
            <span>{daysSinceJoin} days</span>
          </div>
        </div>

        {/* Node Footer - Simplified without status badges */}
        <div className="node-footer">
            <span className="referral-badge">
              <i className="ri-team-line"></i> View Details
            </span>
        </div>

        {/* Expandable Details */}
        {isExpanded && (
          <div className="node-details">
            <div className="detail-row">
              <span className="detail-label">
                <i className="ri-mail-line"></i> Email:
              </span>
              <span className="detail-value">{node.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <i className="ri-user-line"></i> Role:
              </span>
              <span className="detail-value">{node.role}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <i className="ri-calendar-line"></i> Joined:
              </span>
              <span className="detail-value">{joinDate.toLocaleDateString()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <i className="ri-time-line"></i> Time:
              </span>
              <span className="detail-value">{joinDate.toLocaleTimeString()}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render the matrix tree
  const renderMatrixTree = () => {
    if (!treeData) return null;

    const levels = collectNodesByLevel(treeData);
    
    return (
      <div className="matrix-tree-display">
        {Object.keys(levels).sort((a, b) => parseInt(a) - parseInt(b)).map(levelNum => {
          const levelNodes = levels[levelNum];
          return (
            <div key={levelNum} className="matrix-level">
              <div className="level-header">
                <FontAwesomeIcon icon={levelNum === '0' ? faCrown : levelNum === '1' ? faTrophy : faMedal} />
                {' '}Level {levelNum} ({levelNodes.length} members)
              </div>
              <div className="level-nodes">
                {levelNodes.map(node => renderMatrixNode(node, parseInt(levelNum)))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render statistics dashboard
  const renderStatistics = () => {
    if (!statistics) return null;

    const activeRate = statistics.totalDirectReferrals > 0 
      ? Math.round((statistics.activeDirectReferrals / statistics.totalDirectReferrals) * 100) 
      : 0;

    return (
      <div className="stats-dashboard">
        <div className="portal">
          <div className="portal-header">
            <h4>
              <FontAwesomeIcon icon={faChartLine} />
              {' '}Matrix Network Analytics
            </h4>
            <p>Comprehensive overview of your network structure and performance</p>
          </div>
          <div className="portal-body">
            <div className="row">
              <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                <div className="stat-card stat-primary">
                  <div className="stat-number">{statistics.totalDirectReferrals}</div>
                  <div className="stat-label">Total Network Size</div>
                  <div className="stat-icon">
                    <i className="ri-team-line"></i>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                <div className="stat-card stat-success">
                  <div className="stat-number">{statistics.activeDirectReferrals}</div>
                  <div className="stat-label">Active Members</div>
                  <div className="stat-icon">
                    <i className="ri-user-3-line"></i>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                <div className="stat-card stat-danger">
                  <div className="stat-number">{statistics.inactiveDirectReferrals}</div>
                  <div className="stat-label">Inactive Members</div>
                  <div className="stat-icon">
                    <i className="ri-user-unfollow-line"></i>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                <div className="stat-card stat-warning">
                  <div className="stat-number">{activeRate}%</div>
                  <div className="stat-label">Activity Rate</div>
                  <div className="stat-icon">
                    <i className="ri-pulse-line"></i>
                  </div>
                </div>
              </div>
            </div>

            {statistics.levelsDistribution && (
              <div className="level-distribution">
                <h5>
                  <i className="ri-bar-chart-line"></i>
                  {' '}Level Distribution
                </h5>
                <div className="row">
                  {Object.keys(statistics.levelsDistribution).map(level => {
                    const levelData = statistics.levelsDistribution[level];
                    return (
                      <div key={level} className="col-lg-2 col-md-4 col-sm-6 col-12">
                        <div className="level-stat">
                          <div className="level-number">{levelData.count}</div>
                          <div className="level-label">Level {levelData.level}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="stats-footer">
              <small className="text-muted">
                <i className="ri-time-line"></i>
                {' '}Analytics generated: {new Date(statistics.calculatedAt).toLocaleString()}
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>Matrix Genealogy Tree | {config.appName}</title>
      </Helmet>
      <div className="container">
        <div className="page">
          <div className="page-heading">
            <h1>
              <FontAwesomeIcon icon={faTree} />
              {' '}Matrix Genealogy Tree
            </h1>
            <span>
              <Link to="/"> Dashboard </Link> / Matrix Genealogy Tree
            </span>
          </div>
          <div className="page-content">
            {/* Control Panel */}
            <div className="portal">
              <div className="portal-header">
                <h4>
                  <i className="ri-settings-line"></i>
                  {' '}Control Panel
                </h4>
              </div>
              <div className="portal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12">
                      <div className="mb-3">
                        <label className="form-label">
                          <i className="ri-user-line"></i>
                          {' '}User ID
                        </label>
                        <input
                          className="form-control"
                          type="number"
                          value={userId}
                          onChange={(e) => setUserId(parseInt(e.target.value) || '')}
                          min="1"
                          placeholder="Enter User ID"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12 d-none">
                      <div className="mb-3">
                        <label className="form-label">
                          <i className="ri-bar-chart-line"></i>
                          {' '}Max Depth
                        </label>
                        <select
                          className="form-control"
                          value={maxDepth}
                          onChange={(e) => setMaxDepth(parseInt(e.target.value))}
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(depth => (
                            <option key={depth} value={depth}>{depth} Levels</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12 d-none">
                      <div className="mb-3">
                        <label className="form-label">
                          <i className="ri-team-line"></i>
                          {' '}Max Children
                        </label>
                        <select
                          className="form-control"
                          value={maxChildren}
                          onChange={(e) => setMaxChildren(parseInt(e.target.value))}
                        >
                          {[2, 4, 6, 8, 10, 12, 15, 20].map(count => (
                            <option key={count} value={count}>{count} Children</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-6 col-12 d-none">
                      <div className="mb-3">
                        <label className="form-label">
                          <i className="ri-eye-line"></i>
                          {' '}View Mode
                        </label>
                        <select
                          className="form-control"
                          value={viewMode}
                          onChange={(e) => setViewMode(e.target.value)}
                        >
                          <option value="basic">Basic Tree</option>
                          <option value="stats">With Statistics</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12 text-end">
                      <div className="mb-3">
                        <button 
                          type="button" 
                          className="btn btn-danger btn-md me-2"
                          onClick={clearResults}
                        >
                          <i className="ri-delete-bin-line"></i> Clear
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-warning btn-md me-2"
                          onClick={exportTreeData}
                          disabled={!treeData}
                        >
                          <FontAwesomeIcon icon={faDownload} /> Export
                        </button>
                        <button type="submit" className="btn btn-success btn-md">
                          <FontAwesomeIcon icon={faTree} /> Load Tree
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* Statistics Dashboard */}
            {statistics && renderStatistics()}

            {/* Legend */}
            {treeData && (
              <div className="portal mt-3 mb-3 d-none">
                <div className="portal-header">
                  <h4>
                    <i className="ri-information-line"></i>
                    {' '}Legend
                  </h4>
                </div>
                <div className="portal-body">
                  <div className="legend-container">
                    <div className="legend-item">
                      <div className="legend-color legend-root"></div>
                      <span>
                        <FontAwesomeIcon icon={faCrown} />
                        {' '}Root User
                      </span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color legend-level1"></div>
                      <span>
                        <FontAwesomeIcon icon={faTrophy} />
                        {' '}Level 1
                      </span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color legend-level2"></div>
                      <span>
                        <FontAwesomeIcon icon={faMedal} />
                        {' '}Level 2
                      </span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color legend-level3"></div>
                      <span>
                        <FontAwesomeIcon icon={faMedal} />
                        {' '}Level 3+
                      </span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color legend-active"></div>
                      <span>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {' '}Active
                      </span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color legend-inactive"></div>
                      <span>
                        <FontAwesomeIcon icon={faTimesCircle} />
                        {' '}Inactive
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Matrix Tree Display */}
            {treeData && (
              <div className="portal">
                <div className="portal-header">
                  <h4>
                    <FontAwesomeIcon icon={faTree} />
                    {' '}Matrix Tree Structure
                  </h4>
                  <span className="badge bg-primary">User ID: {userId}</span>
                </div>
                <div className="portal-body">
                  <div className="matrix-tree-container">
                    {renderMatrixTree()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}